import { executePostData } from '../../../services/ApiBase';
import { ConfigUrl } from '../../../auth/OidcConfig';
import { saveDraft, publishDraft } from '../../tradeTemplates/services/TradeTemplateVersionService';

export interface SaveTradeTemplatePayload {
  /** Display name (optional server-side; carried from prior version if omitted). */
  name: string;
  /** Task-template JSON to persist in the trade_template `tasks` column. */
  tasks: string;
  /** Tenants to publish the new version to (may be empty = provider master only). */
  tenantIds: string[];
  /** Operator performing the save. */
  updatedBy: string;
}

/**
 * Save the trade template as a new version: provider master (trade_template_master)
 * plus a new version in each selected tenant's trade_template.
 *
 * Backend: POST /taskMapping/saveTradeTemplate/tradeId/{tradeId}
 * Returns { providerTemplateId, tenants: [{ tenantId, status, tenantTemplateId | error }] }.
 */
export async function saveTradeTemplate(
  tradeId: number,
  payload: SaveTradeTemplatePayload,
  accessToken: string,
) {
  return executePostData(
    `${ConfigUrl.baseURL}/taskMapping/saveTradeTemplate/tradeId/${tradeId}`,
    payload,
    accessToken,
  );
}

/**
 * Save the mapped task template to ONE scope (never both), matching the "save to the respective
 * tenant or provider" rule:
 *   • tenantId === null → provider default only (provider master, no tenant push).
 *   • tenantId set      → that tenant only, via draft→publish (so provider master is NOT written).
 * Returns an ApiResult ({ ok, message?, data? }); the caller only needs `.ok`.
 */
export async function saveTaskTemplateToScope(
  tradeId: number,
  tenantId: string | null,
  payload: { name: string; tasks: string; updatedBy: string; note?: string },
  accessToken: string,
) {
  if (!tenantId) {
    return saveTradeTemplate(
      tradeId,
      { name: payload.name, tasks: payload.tasks, tenantIds: [], updatedBy: payload.updatedBy },
      accessToken,
    );
  }
  const draft = await saveDraft(
    tradeId,
    { name: payload.name, tasks: payload.tasks, scope: 'tenant', tenantId, updatedBy: payload.updatedBy },
    accessToken,
  );
  if (!draft.ok) return draft;
  return publishDraft(
    tradeId,
    { scope: 'tenant', tenantId, updatedBy: payload.updatedBy, note: payload.note ?? 'Mapping update' },
    accessToken,
  );
}
